import { debounce } from 'lodash';
import api from '@js/api';
import ErrorHandler from '@js/ErrorHandler';
import {
	getUserData,
	checkEmailExists,
	validatePostalCode,
	getUserAddresses,
	getCartData,
	updateCartShippingAddress,
	updateCartShippingMethod,
	determineGateway,
	addOfferToCart,
	removeOfferFromCart,
	areAddressesEqual,
} from '@js/service/checkoutServiceV3';

export default {
	async initialize({
		commit,
		state
	}, refreshUserData = false) {
		let user = null;
		if (refreshUserData) {
			user = await getUserData();
		} else {
			user = window.user;
		}

		if (user) {
			commit('setUser', user);

			const {
				billingAddress,
				shippingAddress
			} =
				await getUserAddresses();

			if (billingAddress.id) {
				commit('setBillingAddress', { ...billingAddress });
			}
			if (shippingAddress.id) {
				commit('setShippingAddress', { ...shippingAddress });
			}

			commit(
				'setShippingDifferentFromBilling',
				billingAddress.id &&
				shippingAddress.id &&
				billingAddress.id !== shippingAddress.id
			);
		}


		const cartData = await getCartData(state.params, state.query);

		if (!cartData || !cartData.order_offers.length) {
			window.location.href = '/cart';
			return;
		}

		commit('setCart', cartData);

		try {
			const {
				billingComponent,
				billingGateway
			} = determineGateway();
			commit('setBillingComponent', billingComponent);
			commit('setBillingGateway', billingGateway);
		} catch (error) {
			ErrorHandler.reportError(error.message);
		}

		commit('setInitialized', true);
	},

	async addOfferToCart({ commit }, offerId) {
		commit('setModifyingCart', true);
		const { data } = await addOfferToCart(offerId);
		commit('setCart', data.data);
		commit('setModifyingCart', false);
	},

	async removeOfferFromCart({
		commit,
		getters
	}, offerId) {
		commit('setModifyingCart', true);

		const cartItem = getters.itemForOffer(offerId);

		if (cartItem) {
			const { data } = await removeOfferFromCart(cartItem.id);
			commit('setCart', data);
		}

		commit('setModifyingCart', false);
	},

	updateShippingMethod: debounce(async ({ commit }, shippingMethodId) => {
		commit('setModifyingCart', true);
		commit('setCart', await updateCartShippingMethod(shippingMethodId));
		commit('setModifyingCart', false);
	}, 500),

	updateCartShippingAddress: debounce(
		async ({
			state,
			getters,
			commit
		}) => {
			if (getters.mainAddressErrors.length > 0) return;

			if (
				state.cart &&
				state.cart.shipping_address &&
				areAddressesEqual(
					state.cart.shipping_address,
					getters.mainAddress
				)
			) {
				return;
			}

			const cart = await updateCartShippingAddress(
				getters.mainAddress
			);

			commit('setCart', cart);
		},
		500
	),

	async checkEmail({
		state,
		commit
	}) {
		commit('setEmailExistsError', await checkEmailExists(state.email));
	},

	async checkBillingPostalCode({
		state,
		commit
	}) {
		commit(
			'setBillingPostalCodeMismatchError',
			!(await validatePostalCode(state.billingAddress))
		);
	},

	async checkShippingPostalCode({
		state,
		commit
	}) {
		commit(
			'setShippingPostalCodeMismatchError',
			!(await validatePostalCode(state.shippingAddress))
		);
	},

	async submitOrder({
		state,
		getters,
		commit,
		dispatch
	}) {
		commit('setSubmittingOrder', true);

		if (state.email) {
			await dispatch('checkEmail');
		}

		if (getters.billingAddressVisible && !getters.hasBillingAddressErrors) {
			await dispatch('checkBillingPostalCode');
		}

		if (getters.shippingAddressVisible && !getters.hasShippingAddressErrors) {
			await dispatch('checkShippingPostalCode');
		}

		if (getters.formHasErrors) {
			commit('setSubmittingOrder', false);
			commit('setShowFormErrors', true);
			window.scrollTo({
				top: 0,
				behavior: 'smooth'
			});
			return;
		}

		dispatch('completeOrder');
	},

	async payCart({
		state,
		getters,
		commit
	}, { payment }) {
		const user = {
			first_name: getters.mainAddress.first_name,
			last_name: getters.mainAddress.last_name,
			sms_opt_in: false,
		};

		if (!state.user) {
			user.email = state.email;
			user.password = state.password;
			user.password_confirmation = state.password;
		} else {
			user.email = state.user.email;
			user.sms_opt_in = state.user.sms_opt_in;
		}

		try {
			const response = await api.payCart({
				payment,
				user,
				billing_address: getters.addressToBill,
				shipping_address: getters.addressToShip,
			});

			window.location.href = response.meta.next_url;
		} catch (error) {
			commit(
				'setErrors',
				ErrorHandler.parseResponseError(
					error,
					'Something went wrong, please contact customer support at support@sculptnation.com to place your order or try again.'
				)
			);
			window.scrollTo({
				top: 0,
				behavior: 'smooth'
			});
			throw error;
		}
	},

	async completeOrder({
		state,
		commit,
		dispatch
	}) {
		try {
			const {
				token,
				gateway,
				details
			} = await state.getToken();
			await dispatch('payCart', {
				payment: {
					token,
					gateway,
					details
				}
			});
		} catch (error) {
			commit('setSubmittingOrder', false);
		}
	},

	async paypalCompleteOrder({
		commit,
		dispatch
	}, payload) {
		commit('setProcessingPaypal', true);

		try {
			await dispatch('payCart', {
				payment: {
					token: payload.nonce,
					gateway: 'Braintree',
					paypal: {
						email: payload.details.email,
					},
				}
			});
		} catch (error) {
			commit('setProcessingPaypal', false);
		}
	},
};
